import { MapUtils } from '../../../utility/map-utils.js';
import { generateUniqueDatabaseId } from '../../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { TimeZone } from '../../../time/timezone.js';
import { Dayjs } from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { InvoiceProvider } from './invoice-provider.js';
import { TrueTime } from '../../../index.js';

export enum InvoiceSubscriptionRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED',
}

export class InvoiceSubscriptionRequest extends DataModel {
  static readonly TYPE_ID: string = '75a01f1977df403888cadf2e19a604c4';

  constructor(
    public readonly uniqueId: string,
    public readonly creationDate: Dayjs,
    public readonly status: InvoiceSubscriptionRequestStatus,
    public readonly organizationId: string,
    public readonly paymentAccountId: string,
    public readonly paymentAccountName: string,
    public readonly invoiceProvider: InvoiceProvider,
  ) {
    super();
  }

  get $typeId(): string {
    return InvoiceSubscriptionRequest.TYPE_ID;
  }

  static create(
      organizationId: string,
      paymentAccountId: string,
      paymentAccountName: string,
      invoiceProvider: InvoiceProvider,
  ): InvoiceSubscriptionRequest {
    return new InvoiceSubscriptionRequest(
        generateUniqueDatabaseId(),
        TrueTime.now(),
        InvoiceSubscriptionRequestStatus.Pending,
        organizationId,
        paymentAccountId,
        paymentAccountName,
        invoiceProvider,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): InvoiceSubscriptionRequest {
    return new InvoiceSubscriptionRequest(
        MapUtils.getString(map, 'uniqueId'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
        toEnumFromString(MapUtils.getString(map, '$status'), InvoiceSubscriptionRequestStatus),
        MapUtils.getString(map, 'organizationId'),
        MapUtils.getString(map, 'paymentAccountId'),
        MapUtils.getString(map, 'paymentAccountName'),
        toEnumFromString(MapUtils.getString(map, 'invoiceProvider'), InvoiceProvider),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'organizationId': this.organizationId,
      'paymentAccountId': this.paymentAccountId,
      'paymentAccountName': this.paymentAccountName,
      'invoiceProvider': fromEnumAsString(this.invoiceProvider),
    };
  }

  clone(): this {
    return new InvoiceSubscriptionRequest(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.status,
        this.organizationId,
        this.paymentAccountId,
        this.paymentAccountName,
        this.invoiceProvider,
    ) as this;
  }

  isSame(other: InvoiceSubscriptionRequest): boolean {
    if(this === other) {
      return true;
    }
    return other.uniqueId === this.uniqueId &&
      other.creationDate === this.creationDate &&
      other.status === this.status &&
      other.organizationId === this.organizationId &&
      other.paymentAccountId === this.paymentAccountId &&
      other.paymentAccountName === this.paymentAccountName &&
      other.invoiceProvider === this.invoiceProvider;
  }

  copyWith(data: Partial<InvoiceSubscriptionRequest>): this {
    return new InvoiceSubscriptionRequest(
        data.uniqueId ?? this.uniqueId,
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.status ?? this.status,
        data.organizationId ?? this.organizationId,
        data.paymentAccountId ?? this.paymentAccountId,
        data.paymentAccountName ?? this.paymentAccountName,
        data.invoiceProvider ?? this.invoiceProvider,
    ) as this;
  }

  static encodedStatusSafe(status: InvoiceSubscriptionRequestStatus): string {
    return fromEnumAsString(status);
  }
}

export class InvoiceSubscriptionRequestBuilder extends DataModelBuilder<InvoiceSubscriptionRequest> {
  build(context: SerializationContext, map: SerializedData): InvoiceSubscriptionRequest {
    return InvoiceSubscriptionRequest.fromMap(context, map);
  }
}
